import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  RefreshCw,
  Loader2,
  Download,
  TrendingUp,
  Receipt,
  DollarSign,
  PiggyBank,
  Landmark,
  Coins,
  GraduationCap,
  AlertTriangle,
  FileSpreadsheet,
  Info,
  Users,
} from 'lucide-react';
import {
  fetchRevenueDashboard,
  fetchMonthExpected,
  fetchLoanPortfolioSummary,
  fetchAdvancePortfolioSummary,
  downloadRevenueExcel,
  downloadMonthlyRevenueExcel,
  downloadLoanPortfolioExcel,
  downloadAdvancePortfolioExcel,
} from './Api';

// ---------------------------------------------------------------------
// Reports dashboard: company revenue, loans, advances and school fees
// advances, with Excel downloads for the detail.
//
// Revenue figures come from performance analysis by default (by period
// end); the toggle switches to allocations (by allocation date). A second
// toggle chooses how loan revenue is worked out: Cash basis (each repayment
// x its loan's interest share) or Accrual (interest earned monthly over the
// loan term, skipped while the loan is defaulted). The
// download button follows the period filter: one month -> that month's
// file, this year -> the year's file, custom -> that range's file.
//
//   Revenue  -> /reports/dashboard/revenue/   (date range, per month)
//   Expected -> /reports/dashboard/expected/  (one call per month, lazy)
//   Loans    -> /reports/loan-portfolio/?preview=true    (as of range end)
//   Advances -> /reports/advance-portfolio/?preview=true (as of today)
//
// Loan revenue comes from the backend's loan_revenue field. It is 0 until
// the formula is added there (loan_revenue_by_month in dashboard_views.py);
// the backend's loan_revenue_ready flag hides the "pending" badges then.
// ---------------------------------------------------------------------

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const CATEGORIES = ['Performing', 'Underperforming', 'Defaulted'];
const LOAN_EXTRA_CATEGORIES = ['No loan record', 'Check status'];

const CATEGORY_STYLE = {
  Performing: { bar: 'bg-emerald-500', text: (d) => (d ? 'text-emerald-400' : 'text-emerald-700') },
  Underperforming: { bar: 'bg-amber-400', text: (d) => (d ? 'text-amber-300' : 'text-amber-700') },
  Defaulted: { bar: 'bg-red-500', text: (d) => (d ? 'text-red-400' : 'text-red-700') },
  'No loan record': { bar: 'bg-gray-400', text: (d) => (d ? 'text-gray-400' : 'text-gray-600') },
  'Check status': { bar: 'bg-gray-300', text: (d) => (d ? 'text-gray-400' : 'text-gray-600') },
};

// Top strip colour of each KPI card.
const ACCENTS = {
  maroon: 'from-rose-900 via-rose-400 to-rose-100',
  orange: 'from-orange-600 via-orange-300 to-orange-50',
  green: 'from-emerald-700 via-emerald-300 to-emerald-50',
  amber: 'from-amber-600 via-amber-300 to-amber-50',
  olive: 'from-lime-800 via-stone-300 to-stone-50',
  blue: 'from-blue-700 via-blue-300 to-blue-50',
  indigo: 'from-indigo-700 via-indigo-300 to-indigo-50',
  red: 'from-red-700 via-red-300 to-red-50',
};

// --------------------------------------------------------------------- dates
// Local YYYY-MM-DD. (toISOString() converts to UTC first, which shifts
// local midnight back a day in Nairobi.)
const toISODate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const today = () => toISODate(new Date());

const PRESETS = {
  month: () => {
    const n = new Date();
    return { start: toISODate(new Date(n.getFullYear(), n.getMonth(), 1)), end: today() };
  },
  year: () => {
    const n = new Date();
    return { start: toISODate(new Date(n.getFullYear(), 0, 1)), end: today() };
  },
  past60: () => {
    const s = new Date();
    s.setDate(s.getDate() - 60);
    return { start: toISODate(s), end: today() };
  },
};

const monthRange = (value) => {
  const [y, m] = value.split('-').map(Number);
  const end = toISODate(new Date(y, m, 0));
  return { start: toISODate(new Date(y, m - 1, 1)), end: end > today() ? today() : end };
};

// Months from Dec 2025 (start of the 2026 data) to the current month, newest first.
const monthOptions = () => {
  const out = [];
  const n = new Date();
  let d = new Date(n.getFullYear(), n.getMonth(), 1);
  const first = new Date(2025, 11, 1);
  while (d >= first) {
    out.push({ value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}` });
    d = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  }
  return out;
};

// --------------------------------------------------------------------- numbers
const formatCurrency = (amount) =>
  `KSh ${Math.round(parseFloat(amount || 0)).toLocaleString()}`;

const formatCompact = (amount) => {
  const v = parseFloat(amount || 0);
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1e6) return `${sign}KSh ${(abs / 1e6).toFixed(abs >= 1e7 ? 1 : 2)}M`;
  if (abs >= 1e3) return `${sign}KSh ${(abs / 1e3).toFixed(1)}k`;
  return `${sign}KSh ${Math.round(abs)}`;
};

const BASES = [
  { key: 'cash', label: 'Cash basis', title: "Loan revenue = each repayment x its loan's interest share" },
  { key: 'accrual', label: 'Accrual', title: 'Loan revenue = interest earned each month over the loan term, none while defaulted' },
];

const SOURCES = [
  { key: 'pa', label: 'Performance analysis', note: 'by meeting date' },
  { key: 'alloc', label: 'Allocations', note: 'by allocation date' },
];

// Label for the revenue download button, following the period filter.
const downloadLabel = (preset, range, selectedMonth) => {
  const d = (s) => new Date(`${s}T00:00:00`);
  const monthYear = (s) => `${MONTH_NAMES[d(s).getMonth()]} ${d(s).getFullYear()}`;
  if (preset === 'month' || (preset === 'byMonth' && selectedMonth)) return `${monthYear(range.start)} report`;
  if (preset === 'year') return `${d(range.start).getFullYear()} report`;
  const short = (s) => d(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  return `Report ${short(range.start)} – ${short(range.end)}`;
};

const pct = (num, den) => (den ? `${((num / den) * 100).toFixed(1)}%` : '—');

const formatDate = (s) =>
  s ? new Date(`${s}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// Performing / Underperforming / Defaulted summary + PAR from a preview summary.
const portfolioStats = (summary, countKey) => {
  if (!summary) return null;
  const get = (cat) => summary[cat] || { [countKey]: 0, balance: 0 };
  const cats = [...CATEGORIES, ...LOAN_EXTRA_CATEGORIES].filter((c) => summary[c] !== undefined);
  const rows = cats.map((c) => ({ category: c, count: get(c)[countKey] || 0, balance: get(c).balance || 0 }));
  const classified = CATEGORIES.reduce((s, c) => s + (get(c).balance || 0), 0);
  const at30 = (get('Underperforming').balance || 0) + (get('Defaulted').balance || 0);
  const at90 = get('Defaulted').balance || 0;
  return {
    rows,
    totalCount: rows.reduce((s, r) => s + r.count, 0),
    totalBalance: rows.reduce((s, r) => s + r.balance, 0),
    classified,
    par30: classified ? at30 / classified : 0,
    par90: classified ? at90 / classified : 0,
  };
};

// --------------------------------------------------------------------- pieces
const KpiCard = ({ isDark, label, icon: Icon, accent, value, sub, children, loading }) => (
  <div
    className={`relative overflow-hidden rounded-2xl border p-5 ${
      isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-rose-100'
    }`}
  >
    <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${ACCENTS[accent] || ACCENTS.blue}`} />
    <div className="flex items-start justify-between gap-3">
      <span className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-stone-500'}`}>
        {label}
      </span>
      {Icon && <Icon className={`w-6 h-6 flex-shrink-0 ${isDark ? 'text-gray-300' : 'text-stone-700'}`} />}
    </div>
    <div className={`mt-3 text-3xl font-extrabold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
      {loading ? <Loader2 className="w-6 h-6 animate-spin text-gray-400" /> : value}
    </div>
    {sub && <div className={`mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-stone-500'}`}>{sub}</div>}
    {children}
  </div>
);

const SectionHeader = ({ isDark, icon: Icon, title, subtitle, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-3 mb-4 mt-10">
    <div>
      <h2 className={`text-xl font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        {Icon && <Icon className="w-5 h-5" />}
        {title}
      </h2>
      {subtitle && <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{subtitle}</p>}
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

const DownloadButton = ({ isDark, onClick, busy, children, small }) => (
  <button
    onClick={onClick}
    disabled={busy}
    className={`inline-flex items-center gap-2 rounded-lg border transition-colors disabled:opacity-60 ${
      small ? 'px-2.5 py-1 text-xs' : 'px-4 py-2 text-sm'
    } ${
      isDark
        ? 'border-gray-600 text-gray-200 hover:bg-gray-700'
        : 'border-emerald-200 text-emerald-800 bg-emerald-50 hover:bg-emerald-100'
    }`}
  >
    {busy ? <Loader2 className={`${small ? 'w-3.5 h-3.5' : 'w-4 h-4'} animate-spin`} /> : <Download className={small ? 'w-3.5 h-3.5' : 'w-4 h-4'} />}
    {children}
  </button>
);

const PendingBadge = ({ isDark }) => (
  <span
    className={`ml-1 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
      isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-500'
    }`}
    title="Loan revenue formula not added yet - shown as 0"
  >
    pending
  </span>
);

const ErrorBox = ({ isDark, message, onRetry }) => (
  <div className={`rounded-xl p-4 text-sm flex items-start gap-3 ${isDark ? 'bg-red-900/20 border border-red-800 text-red-400' : 'bg-red-50 border border-red-200 text-red-700'}`}>
    <AlertTriangle className="w-5 h-5 flex-shrink-0" />
    <div className="flex-1">{message}</div>
    {onRetry && (
      <button onClick={onRetry} className="underline font-medium">
        Retry
      </button>
    )}
  </div>
);

// Stacked bar of balances by category + a legend table.
const PortfolioBreakdown = ({ isDark, stats, countLabel }) => {
  if (!stats) return null;
  const total = stats.totalBalance || 1;
  return (
    <div className={`rounded-2xl border p-5 ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-rose-100'}`}>
      <div className={`flex h-4 w-full overflow-hidden rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
        {stats.rows.map((r) =>
          r.balance > 0 ? (
            <div
              key={r.category}
              className={CATEGORY_STYLE[r.category].bar}
              style={{ width: `${(r.balance / total) * 100}%` }}
              title={`${r.category}: ${formatCurrency(r.balance)}`}
            />
          ) : null
        )}
      </div>
      <table className="w-full mt-4 text-sm">
        <thead>
          <tr className={isDark ? 'text-gray-400' : 'text-gray-500'}>
            <th className="text-left font-medium py-1">Category</th>
            <th className="text-right font-medium py-1">{countLabel}</th>
            <th className="text-right font-medium py-1">Balance</th>
            <th className="text-right font-medium py-1">Share</th>
          </tr>
        </thead>
        <tbody>
          {stats.rows.map((r) => (
            <tr key={r.category} className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
              <td className="py-2">
                <span className={`inline-block w-2.5 h-2.5 rounded-full mr-2 ${CATEGORY_STYLE[r.category].bar}`} />
                <span className={`font-medium ${CATEGORY_STYLE[r.category].text(isDark)}`}>{r.category}</span>
              </td>
              <td className={`py-2 text-right ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{r.count}</td>
              <td className={`py-2 text-right font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{formatCurrency(r.balance)}</td>
              <td className={`py-2 text-right ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{pct(r.balance, stats.totalBalance)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// --------------------------------------------------------------------- main
const ReportsDashboard = ({ isDark = false }) => {
  const [preset, setPreset] = useState('year'); // 'month' | 'year' | 'past60' | 'byMonth' | 'custom'
  const [source, setSource] = useState('pa'); // 'pa' = performance analysis (default) | 'alloc'
  const [basis, setBasis] = useState('cash'); // loan revenue: 'cash' (default) | 'accrual'
  const [range, setRange] = useState(PRESETS.year());
  const [selectedMonth, setSelectedMonth] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);

  const [revenue, setRevenue] = useState(null);
  const [revenueLoading, setRevenueLoading] = useState(true);
  const [revenueError, setRevenueError] = useState(null);

  const [expected, setExpected] = useState({}); // { 'YYYY-MM': { loading, data, error } }

  const [loans, setLoans] = useState(null);
  const [loansLoading, setLoansLoading] = useState(true);
  const [loansError, setLoansError] = useState(null);

  const [advances, setAdvances] = useState(null);
  const [advancesLoading, setAdvancesLoading] = useState(true);
  const [advancesError, setAdvancesError] = useState(null);

  const [downloading, setDownloading] = useState({});
  const [downloadError, setDownloadError] = useState(null);

  const runId = useRef(0);
  const months = useMemo(monthOptions, []);
  const loanAsOf = range.end > today() ? today() : range.end;

  // ----------------------------------------------------------- loading
  const loadExpected = useCallback(async (monthRows, id) => {
    // Two months at a time - each call is fairly heavy on the server.
    const queue = [...monthRows];
    const worker = async () => {
      while (queue.length && runId.current === id) {
        const m = queue.shift();
        setExpected((prev) => ({ ...prev, [m.month]: { loading: true } }));
        try {
          const data = await fetchMonthExpected(m.year, m.month_number);
          if (runId.current === id) setExpected((prev) => ({ ...prev, [m.month]: { data } }));
        } catch (err) {
          if (runId.current === id) setExpected((prev) => ({ ...prev, [m.month]: { error: true } }));
        }
      }
    };
    await Promise.all([worker(), worker()]);
  }, []);

  const loadRevenue = useCallback(async (id) => {
    setRevenueLoading(true);
    setRevenueError(null);
    try {
      const data = await fetchRevenueDashboard(range.start, range.end, source, basis);
      if (runId.current !== id) return;
      setRevenue(data);
      setExpected({});
      loadExpected(data.months || [], id);
    } catch (err) {
      if (runId.current === id) setRevenueError(err.response?.data?.error || err.message || 'Failed to load revenue.');
    } finally {
      if (runId.current === id) setRevenueLoading(false);
    }
  }, [range, source, basis, loadExpected]);

  const loadLoans = useCallback(async (id) => {
    setLoansLoading(true);
    setLoansError(null);
    try {
      const data = await fetchLoanPortfolioSummary(loanAsOf);
      if (runId.current === id) setLoans(data);
    } catch (err) {
      if (runId.current === id) setLoansError(err.response?.data?.error || err.message || 'Failed to load the loan portfolio.');
    } finally {
      if (runId.current === id) setLoansLoading(false);
    }
  }, [loanAsOf]);

  const loadAdvances = useCallback(async (id) => {
    setAdvancesLoading(true);
    setAdvancesError(null);
    try {
      const data = await fetchAdvancePortfolioSummary();
      if (runId.current === id) setAdvances(data);
    } catch (err) {
      if (runId.current === id) setAdvancesError(err.response?.data?.error || err.message || 'Failed to load the advance portfolio.');
    } finally {
      if (runId.current === id) setAdvancesLoading(false);
    }
  }, []);

  const loadAll = useCallback(() => {
    runId.current += 1;
    const id = runId.current;
    setUpdatedAt(new Date());
    loadRevenue(id);
    loadLoans(id);
    loadAdvances(id);
  }, [loadRevenue, loadLoans, loadAdvances]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ----------------------------------------------------------- filters
  const choosePreset = (key) => {
    setPreset(key);
    setSelectedMonth('');
    setRange(PRESETS[key]());
  };

  const chooseMonth = (value) => {
    if (!value) return;
    setPreset('byMonth');
    setSelectedMonth(value);
    setRange(monthRange(value));
  };

  const setCustom = (field, value) => {
    if (!value) return;
    setPreset('custom');
    setSelectedMonth('');
    setRange((prev) => {
      const next = { ...prev, [field]: value };
      return next.start > next.end ? { start: next.end, end: next.start } : next;
    });
  };

  // ----------------------------------------------------------- downloads
  const runDownload = async (key, fn) => {
    setDownloading((p) => ({ ...p, [key]: true }));
    setDownloadError(null);
    try {
      await fn();
    } catch (err) {
      setDownloadError(err.message || 'Download failed. Please try again.');
    } finally {
      setDownloading((p) => ({ ...p, [key]: false }));
    }
  };

  const downloadRangeReport = () =>
    runDownload('range', () => downloadRevenueExcel(range.start, range.end, basis));

  // ----------------------------------------------------------- derived
  const totals = revenue?.totals;
  const loanRevenueReady = revenue?.loan_revenue_ready;

  const expectedTotals = useMemo(() => {
    const rows = revenue?.months || [];
    let expInst = 0;
    let expAdv = 0;
    let expSf = 0;
    let loaded = 0;
    rows.forEach((m) => {
      const e = expected[m.month]?.data;
      if (e) {
        expInst += e.expected_inst;
        expAdv += e.expected_min_adv_revenue;
        expSf += e.expected_min_sf_adv_revenue;
        loaded += 1;
      }
    });
    return { expInst, expAdv, expSf, complete: rows.length > 0 && loaded === rows.length, loaded, total: rows.length };
  }, [revenue, expected]);

  const loanStats = useMemo(() => portfolioStats(loans?.summary, 'loans'), [loans]);
  const regAdvStats = useMemo(() => portfolioStats(advances?.summary?.Regular, 'advances'), [advances]);
  const sfAdvStats = useMemo(() => portfolioStats(advances?.summary?.['School fees'], 'advances'), [advances]);

  // ----------------------------------------------------------- styles
  const pill = (active) =>
    `px-4 py-2 rounded-lg border text-sm transition-colors ${
      active
        ? isDark
          ? 'border-rose-400 bg-rose-900/30 text-rose-200'
          : 'border-rose-300 bg-rose-50 text-rose-900'
        : isDark
          ? 'border-gray-600 text-gray-200 hover:border-gray-500'
          : 'border-rose-100 bg-white text-stone-700 hover:border-rose-200'
    }`;

  const field = `px-3 py-2 rounded-lg border text-sm ${
    isDark ? 'bg-[#1e252d] border-gray-600 text-gray-200' : 'bg-white border-rose-100 text-stone-700'
  } focus:outline-none focus:ring-2 focus:ring-rose-200`;

  const th = `px-4 py-3 text-xs font-semibold uppercase tracking-wider whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-700'}`;
  const td = `px-4 py-3 whitespace-nowrap ${isDark ? 'text-gray-200' : 'text-gray-900'}`;
  const panel = `rounded-2xl border overflow-hidden ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-rose-100'}`;

  // ----------------------------------------------------------- render
  return (
    <div className={`pb-32 ${isDark ? '' : 'bg-orange-50/40'}`}>
      {/* ---------------- Filter bar ---------------- */}
      <div className={`rounded-2xl border px-5 py-4 flex flex-wrap items-center gap-3 ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-rose-100'}`}>
        <span className={`text-xs font-semibold uppercase tracking-wider mr-1 ${isDark ? 'text-gray-400' : 'text-stone-500'}`}>
          Period
        </span>
        <button className={pill(preset === 'month')} onClick={() => choosePreset('month')}>This Month</button>
        <button className={pill(preset === 'year')} onClick={() => choosePreset('year')}>This Year</button>
        <button className={pill(preset === 'past60')} onClick={() => choosePreset('past60')}>Past 60 Days</button>
        <select
          value={selectedMonth}
          onChange={(e) => chooseMonth(e.target.value)}
          className={`${field} min-w-[160px] ${preset === 'byMonth' ? (isDark ? 'border-rose-400' : 'border-rose-300 bg-rose-50') : ''}`}
        >
          <option value="">By Month...</option>
          {months.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>

        <span className={`hidden sm:block w-px h-7 ${isDark ? 'bg-gray-600' : 'bg-rose-100'}`} />

        <label className={`text-sm ${isDark ? 'text-gray-400' : 'text-stone-500'}`}>From</label>
        <input type="date" value={range.start} max={today()} onChange={(e) => setCustom('start', e.target.value)} className={field} />
        <label className={`text-sm ${isDark ? 'text-gray-400' : 'text-stone-500'}`}>To</label>
        <input type="date" value={range.end} max={today()} onChange={(e) => setCustom('end', e.target.value)} className={field} />

        <div className="ml-auto flex items-center gap-3">
          {updatedAt && (
            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-stone-500'}`}>
              Updated {updatedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button
            onClick={loadAll}
            title="Refresh"
            className={`p-2 rounded-lg border transition-colors ${isDark ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-rose-100 text-stone-700 hover:bg-rose-50'}`}
          >
            <RefreshCw className={`w-4 h-4 ${revenueLoading || loansLoading || advancesLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {downloadError && (
        <div className="mt-4">
          <ErrorBox isDark={isDark} message={downloadError} />
        </div>
      )}

      {/* ---------------- Revenue ---------------- */}
      <SectionHeader
        isDark={isDark}
        icon={TrendingUp}
        title="Company revenue"
        subtitle={`${formatDate(range.start)} – ${formatDate(range.end)} · from ${
          SOURCES.find((x) => x.key === source).label.toLowerCase()
        } (${SOURCES.find((x) => x.key === source).note}) · loan revenue on ${
          BASES.find((x) => x.key === basis).label.toLowerCase()
        }`}
      >
        <div className={`inline-flex rounded-lg border p-0.5 ${isDark ? 'border-gray-600' : 'border-rose-100 bg-white'}`}>
          {BASES.map((x) => (
            <button
              key={x.key}
              title={x.title}
              onClick={() => setBasis(x.key)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                basis === x.key
                  ? isDark
                    ? 'bg-emerald-900/40 text-emerald-200'
                    : 'bg-emerald-50 text-emerald-900'
                  : isDark
                    ? 'text-gray-300 hover:text-white'
                    : 'text-stone-600 hover:text-stone-900'
              }`}
            >
              {x.label}
            </button>
          ))}
        </div>
        <div className={`inline-flex rounded-lg border p-0.5 ${isDark ? 'border-gray-600' : 'border-rose-100 bg-white'}`}>
          {SOURCES.map((x) => (
            <button
              key={x.key}
              onClick={() => setSource(x.key)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                source === x.key
                  ? isDark
                    ? 'bg-rose-900/40 text-rose-200'
                    : 'bg-rose-50 text-rose-900'
                  : isDark
                    ? 'text-gray-300 hover:text-white'
                    : 'text-stone-600 hover:text-stone-900'
              }`}
            >
              {x.label}
            </button>
          ))}
        </div>
        <DownloadButton isDark={isDark} busy={downloading.range} onClick={downloadRangeReport}>
          {downloadLabel(preset, range, selectedMonth)}
        </DownloadButton>
      </SectionHeader>

      {revenueError ? (
        <ErrorBox isDark={isDark} message={revenueError} onRetry={loadAll} />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
            <KpiCard
              isDark={isDark}
              loading={revenueLoading}
              label="Total revenue"
              icon={DollarSign}
              accent="maroon"
              value={formatCompact(totals?.total_revenue)}
              sub={
                <>
                  Loan {formatCompact(totals?.loan_revenue)}
                  {!loanRevenueReady && <PendingBadge isDark={isDark} />} · Advance {formatCompact(totals?.advance_revenue)} · Others{' '}
                  {formatCompact(totals?.others_revenue)}
                </>
              }
            />
            <KpiCard
              isDark={isDark}
              loading={revenueLoading}
              label="Expenses"
              icon={Receipt}
              accent="orange"
              value={formatCompact(totals?.total_expenses)}
              sub={`Meeting ${formatCompact(totals?.meeting_expenses)} · Office ${formatCompact(totals?.office_expenses)}`}
            />
            <KpiCard
              isDark={isDark}
              loading={revenueLoading}
              label="Final revenue"
              icon={TrendingUp}
              accent="green"
              value={formatCompact(totals?.final_revenue)}
              sub={
                <>
                  <span className={(totals?.final_revenue || 0) >= 0 ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold'}>
                    {pct(totals?.final_revenue, totals?.total_revenue)} margin
                  </span>{' '}
                  · after expenses
                </>
              }
            />
            <KpiCard
              isDark={isDark}
              loading={revenueLoading}
              label="Net savings"
              icon={PiggyBank}
              accent="amber"
              value={formatCompact(totals?.net_savings)}
              sub={`Deposits ${formatCompact(totals?.savings_deposits)} · Withdrawals ${formatCompact(totals?.savings_withdrawals)}`}
            />
            <KpiCard
              isDark={isDark}
              loading={revenueLoading}
              label="Installments collected"
              icon={Landmark}
              accent="olive"
              value={formatCompact(totals?.inst_paid)}
              sub={
                expectedTotals.complete
                  ? `${pct(totals?.inst_paid, expectedTotals.expInst)} of ${formatCompact(expectedTotals.expInst)} expected`
                  : `Expected loading… ${expectedTotals.loaded}/${expectedTotals.total} months`
              }
            />
          </div>

          {/* Monthly breakdown */}
          <div className={`${panel} mt-6`}>
            <div className={`px-5 py-4 border-b flex items-center justify-between ${isDark ? 'border-gray-700' : 'border-rose-100'}`}>
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>By month</h3>
              <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <Info className="w-3.5 h-3.5" />
                Expected figures are for the whole month · the xlsx button downloads that month
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className={`border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-stone-50 border-rose-100'}`}>
                  <tr>
                    <th className={`${th} text-left`}>Month</th>
                    <th className={`${th} text-right`}>Loan rev.</th>
                    <th className={`${th} text-right`}>Advance rev.</th>
                    <th className={`${th} text-right`}>Others</th>
                    <th className={`${th} text-right`}>Total rev.</th>
                    <th className={`${th} text-right`}>Expenses</th>
                    <th className={`${th} text-right`}>Final rev.</th>
                    <th className={`${th} text-right`}>Inst paid</th>
                    <th className={`${th} text-right`}>Expected inst</th>
                    <th className={`${th} text-right`}>Collection</th>
                    <th className={`${th} text-right`}>Adv. rev vs min</th>
                    <th className={`${th} text-center`}>Excel</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-rose-50'}`}>
                  {revenueLoading && (
                    <tr>
                      <td colSpan={12} className="py-10 text-center">
                        <Loader2 className="w-6 h-6 animate-spin text-gray-400 inline" />
                      </td>
                    </tr>
                  )}
                  {!revenueLoading && !(revenue?.months || []).length && (
                    <tr>
                      <td colSpan={12} className={`py-10 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        No months in this period.
                      </td>
                    </tr>
                  )}
                  {!revenueLoading &&
                    (revenue?.months || []).map((m) => {
                      const e = expected[m.month];
                      const expCell = (value) =>
                        e?.loading || (!e?.data && !e?.error) ? (
                          <Loader2 className="w-4 h-4 animate-spin text-gray-400 inline" />
                        ) : e?.error ? (
                          <span className="text-red-500 text-xs">failed</span>
                        ) : (
                          value
                        );
                      return (
                        <tr key={m.month} className={isDark ? 'hover:bg-gray-700/50' : 'hover:bg-rose-50/40'}>
                          <td className={`${td} font-medium`}>
                            {m.label}
                            {m.is_partial && (
                              <span className={`ml-2 text-[10px] uppercase px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-amber-50 text-amber-700'}`} title={`${m.start} to ${m.end}`}>
                                partial
                              </span>
                            )}
                          </td>
                          <td className={`${td} text-right ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{formatCurrency(m.loan_revenue)}</td>
                          <td className={`${td} text-right`}>{formatCurrency(m.advance_revenue)}</td>
                          <td className={`${td} text-right`}>{formatCurrency(m.others_revenue)}</td>
                          <td className={`${td} text-right font-semibold`}>{formatCurrency(m.total_revenue)}</td>
                          <td className={`${td} text-right text-orange-600`}>{formatCurrency(m.total_expenses)}</td>
                          <td className={`${td} text-right font-semibold ${m.final_revenue >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {formatCurrency(m.final_revenue)}
                          </td>
                          <td className={`${td} text-right`}>{formatCurrency(m.inst_paid)}</td>
                          <td className={`${td} text-right`}>{expCell(formatCurrency(e?.data?.expected_inst))}</td>
                          <td className={`${td} text-right`}>{expCell(pct(m.inst_paid, e?.data?.expected_inst))}</td>
                          <td className={`${td} text-right`}>{expCell(pct(m.advance_revenue, e?.data?.expected_min_adv_revenue))}</td>
                          <td className={`${td} text-center`}>
                            <DownloadButton
                              isDark={isDark}
                              small
                              busy={downloading[m.month]}
                              onClick={() => runDownload(m.month, () => downloadMonthlyRevenueExcel(m.year, m.month_number, basis))}
                            >
                              xlsx
                            </DownloadButton>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
                {!revenueLoading && totals && (
                  <tfoot className={`border-t-2 ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-stone-50 border-rose-200'}`}>
                    <tr className="font-bold">
                      <td className={td}>TOTAL</td>
                      <td className={`${td} text-right`}>{formatCurrency(totals.loan_revenue)}</td>
                      <td className={`${td} text-right`}>{formatCurrency(totals.advance_revenue)}</td>
                      <td className={`${td} text-right`}>{formatCurrency(totals.others_revenue)}</td>
                      <td className={`${td} text-right`}>{formatCurrency(totals.total_revenue)}</td>
                      <td className={`${td} text-right text-orange-600`}>{formatCurrency(totals.total_expenses)}</td>
                      <td className={`${td} text-right ${totals.final_revenue >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(totals.final_revenue)}</td>
                      <td className={`${td} text-right`}>{formatCurrency(totals.inst_paid)}</td>
                      <td className={`${td} text-right`}>{expectedTotals.complete ? formatCurrency(expectedTotals.expInst) : '…'}</td>
                      <td className={`${td} text-right`}>{expectedTotals.complete ? pct(totals.inst_paid, expectedTotals.expInst) : '…'}</td>
                      <td className={`${td} text-right`}>{expectedTotals.complete ? pct(totals.advance_revenue, expectedTotals.expAdv) : '…'}</td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Top groups */}
          <div className={`${panel} mt-6`}>
            <div className={`px-5 py-4 border-b flex items-center gap-2 ${isDark ? 'border-gray-700' : 'border-rose-100'}`}>
              <Users className={`w-4 h-4 ${isDark ? 'text-gray-300' : 'text-stone-600'}`} />
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Top groups by revenue</h3>
              <span className={`text-xs ml-auto ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {totals?.group_count || 0} groups · {totals?.member_count || 0} members
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className={`border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-stone-50 border-rose-100'}`}>
                  <tr>
                    <th className={`${th} text-left`}>#</th>
                    <th className={`${th} text-left`}>Group</th>
                    <th className={`${th} text-right`}>Total revenue</th>
                    <th className={`${th} text-right`}>Advance rev.</th>
                    <th className={`${th} text-right`}>Others</th>
                    <th className={`${th} text-right`}>Inst paid</th>
                    <th className={`${th} text-right`}>Savings deposits</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-rose-50'}`}>
                  {(revenue?.top_groups || []).map((g, i) => (
                    <tr key={g.group_id ?? 'none'} className={isDark ? 'hover:bg-gray-700/50' : 'hover:bg-rose-50/40'}>
                      <td className={`${td} ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{i + 1}</td>
                      <td className={`${td} font-medium`}>{g.group_name}</td>
                      <td className={`${td} text-right font-semibold`}>{formatCurrency(g.total_revenue)}</td>
                      <td className={`${td} text-right`}>{formatCurrency(g.advance_revenue)}</td>
                      <td className={`${td} text-right`}>{formatCurrency(g.others_revenue)}</td>
                      <td className={`${td} text-right`}>{formatCurrency(g.inst_paid)}</td>
                      <td className={`${td} text-right`}>{formatCurrency(g.savings_deposits)}</td>
                    </tr>
                  ))}
                  {!revenueLoading && !(revenue?.top_groups || []).length && (
                    <tr>
                      <td colSpan={7} className={`py-8 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No data in this period.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ---------------- Loans ---------------- */}
      <SectionHeader
        isDark={isDark}
        icon={Landmark}
        title="Loan portfolio"
        subtitle={`As of ${formatDate(loans?.as_of || loanAsOf)} · performing < 1 installment behind, underperforming 1–3, defaulted 3+`}
      >
        <DownloadButton
          isDark={isDark}
          busy={downloading.loans}
          onClick={() => runDownload('loans', () => downloadLoanPortfolioExcel(loanAsOf))}
        >
          Loan portfolio Excel
        </DownloadButton>
      </SectionHeader>

      {loansError ? (
        <ErrorBox isDark={isDark} message={loansError} onRetry={loadAll} />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
            <KpiCard isDark={isDark} loading={loansLoading} label="Loan book" icon={Landmark} accent="blue"
              value={formatCompact(loanStats?.totalBalance)} sub={`${loanStats?.totalCount || 0} members with a loan balance`} />
            {CATEGORIES.map((c, i) => {
              const row = loanStats?.rows.find((r) => r.category === c);
              return (
                <KpiCard key={c} isDark={isDark} loading={loansLoading} label={c} icon={i === 2 ? AlertTriangle : Coins}
                  accent={['green', 'amber', 'red'][i]} value={row?.count ?? 0}
                  sub={`${formatCurrency(row?.balance)} · ${pct(row?.balance, loanStats?.classified)}`} />
              );
            })}
            <KpiCard isDark={isDark} loading={loansLoading} label="Portfolio at risk" icon={AlertTriangle} accent="maroon"
              value={`${((loanStats?.par30 || 0) * 100).toFixed(1)}%`}
              sub={`PAR30 · PAR90 ${((loanStats?.par90 || 0) * 100).toFixed(1)}%`} />
          </div>
          <div className="mt-4">
            {!loansLoading && <PortfolioBreakdown isDark={isDark} stats={loanStats} countLabel="Loans" />}
          </div>
          {!loanRevenueReady && (
            <p className={`mt-3 text-xs flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              <Info className="w-3.5 h-3.5" /> Loan revenue is shown as 0 until its formula is added.
            </p>
          )}
        </>
      )}

      {/* ---------------- Advances (regular) ---------------- */}
      <SectionHeader
        isDark={isDark}
        icon={Coins}
        title="Advance portfolio (regular)"
        subtitle={`As of ${formatDate(advances?.as_of || today())} · by meetings missed in a row (needs at least 10% of principal per meeting)`}
      >
        <DownloadButton isDark={isDark} busy={downloading.advances}
          onClick={() => runDownload('advances', () => downloadAdvancePortfolioExcel())}>
          Advance portfolio Excel
        </DownloadButton>
      </SectionHeader>

      {advancesError ? (
        <ErrorBox isDark={isDark} message={advancesError} onRetry={loadAll} />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-4">
            <KpiCard isDark={isDark} loading={advancesLoading} label="Advance book" icon={Coins} accent="blue"
              value={formatCompact(regAdvStats?.totalBalance)}
              sub={`${regAdvStats?.totalCount || 0} advances with a balance`} />
            <KpiCard isDark={isDark} loading={revenueLoading} label="Advance revenue" icon={DollarSign} accent="maroon"
              value={formatCompact(totals?.advance_revenue)}
              sub={
                expectedTotals.complete
                  ? `${pct(totals?.advance_revenue, expectedTotals.expAdv)} of ${formatCompact(expectedTotals.expAdv)} minimum`
                  : `Interest ${formatCompact(totals?.int_paid)} · Adva paid ${formatCompact(totals?.adv_paid)}`
              } />
            {CATEGORIES.map((c, i) => {
              const row = regAdvStats?.rows.find((r) => r.category === c);
              return (
                <KpiCard key={c} isDark={isDark} loading={advancesLoading} label={c} icon={i === 2 ? AlertTriangle : Coins}
                  accent={['green', 'amber', 'red'][i]} value={row?.count ?? 0}
                  sub={`${formatCurrency(row?.balance)} · ${pct(row?.balance, regAdvStats?.classified)}`} />
              );
            })}
            <KpiCard isDark={isDark} loading={advancesLoading} label="Portfolio at risk" icon={AlertTriangle} accent="maroon"
              value={`${((regAdvStats?.par30 || 0) * 100).toFixed(1)}%`}
              sub={`PAR30 · PAR90 ${((regAdvStats?.par90 || 0) * 100).toFixed(1)}%`} />
          </div>
          <div className="mt-4">
            {!advancesLoading && <PortfolioBreakdown isDark={isDark} stats={regAdvStats} countLabel="Advances" />}
          </div>
        </>
      )}

      {/* ---------------- School fees advances ---------------- */}
      <SectionHeader
        isDark={isDark}
        icon={GraduationCap}
        title="School fees advances"
        subtitle="Members' revenue - not counted in company revenue"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-4">
        <KpiCard isDark={isDark} loading={advancesLoading} label="SF advance book" icon={Coins} accent="blue"
          value={formatCompact(sfAdvStats?.totalBalance)}
          sub={`${sfAdvStats?.totalCount || 0} advances · PAR30 ${((sfAdvStats?.par30 || 0) * 100).toFixed(1)}%`} />
        <KpiCard isDark={isDark} loading={revenueLoading} label="SF advance revenue" icon={GraduationCap} accent="indigo"
          value={formatCompact(totals?.sf_advance_revenue)}
          sub={
            expectedTotals.complete
              ? `${pct(totals?.sf_advance_revenue, expectedTotals.expSf)} of ${formatCompact(expectedTotals.expSf)} minimum`
              : `Interest ${formatCompact(totals?.sf_int_paid)} · Adva paid ${formatCompact(totals?.sf_adv_paid)}`
          } />
        <KpiCard isDark={isDark} loading={revenueLoading} label="SF savings deposits" icon={PiggyBank} accent="blue"
          value={formatCompact(totals?.sf_savings_deposits)} sub="School fees savings paid in" />
        {CATEGORIES.map((c, i) => {
          const row = sfAdvStats?.rows.find((r) => r.category === c);
          return (
            <KpiCard key={c} isDark={isDark} loading={advancesLoading} label={`SF ${c}`} icon={i === 2 ? AlertTriangle : Coins}
              accent={['green', 'amber', 'red'][i]} value={row?.count ?? 0}
              sub={`${formatCurrency(row?.balance)} · ${pct(row?.balance, sfAdvStats?.classified)}`} />
          );
        })}
      </div>
      {!advancesLoading && !advancesError && (
        <div className="mt-4">
          <PortfolioBreakdown isDark={isDark} stats={sfAdvStats} countLabel="Advances" />
        </div>
      )}

      <p className={`mt-8 text-xs flex items-center gap-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
        <FileSpreadsheet className="w-3.5 h-3.5" />
        The Excel files hold the detail: per-group tables, the performance-analysis comparison, expenses, withdrawals and per-member figures.
      </p>
    </div>
  );
};

export default ReportsDashboard;